import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import DataTable, { type Column } from '../components/DataTable'
import HorarioDeAtencion from '../components/HorarioDeAtencion'
import type { Advisor } from '../types/portal'
import { QUE_ES_UN_CLIENTE_ESPERANDO } from '../sla'

/**
 * "Asesores" — quién está recibiendo, cuánta carga tiene y en qué posición de
 * la rotación quedó.
 *
 * La posición la calcula el backend con la misma función que usa el
 * round-robin (`_orden_rotacion`), así que lo que se ve acá es exactamente el
 * orden en que se van a repartir las próximas conversaciones. Si se calculara
 * aparte, la tabla y el reparto real podrían discrepar.
 *
 * Y quién NO está atendiendo (DEF-007, 01-oct-2026). Desde ese día toda
 * conversación queda asignada a un asesor aunque esté ocupado o en «No
 * disponible», y se queda ahí hasta que un supervisor la reasigne: la cola dejó
 * de ser donde se ve a un cliente sin atender. La columna «Atención» lo dice
 * por persona, con la MISMA cuenta que las incidencias por asesor
 * (`negocio.por_asesor`): si dijera otra cosa, el supervisor no sabría a cuál
 * creerle.
 *
 * Desde el 20-oct-2026 (documento de QA «Asignación de conversaciones») la
 * carga cuenta solo lo que atiende una PERSONA, el tope de 5 es un indicador
 * —no interviene en el reparto— y el horario de atención, que pausa el reloj
 * del asesor, se edita acá (`HorarioDeAtencion`).
 *
 * Y se refresca sola (DEF-112, QA de TEST 22-oct-2026): se cargaba una vez al
 * entrar, así que «El que más espera: 55 min» convivía con los 58 que ya
 * decía el Panel en el mismo minuto. Cada `REFRESCO_MS`, sin el «Cargando…»
 * de la primera carga.
 */
const REFRESCO_MS = 30_000

const TOPE_POR_DEFECTO = 5

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`
}

/** Los motivos por los que este asesor no está atendiendo, ya redactados.
 *  Vacío = nada que reasignar. */
function motivosSinAtender(a: Advisor): string[] {
  const motivos: string[] = []
  const asignadas = a.assigned_live ?? 0
  if (!a.available && asignadas > 0) {
    motivos.push(`No disponible con ${plural(asignadas, 'asignada', 'asignadas')}`)
  }
  const esperando = a.waiting_clients ?? 0
  if (esperando > 0) {
    motivos.push(`${plural(esperando, 'cliente esperando', 'clientes esperando')} respuesta fuera de plazo`)
  }
  return motivos
}

export default function Advisors() {
  const [advisors, setAdvisors] = useState<Advisor[]>([])
  const [tope, setTope] = useState(TOPE_POR_DEFECTO)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true)
    try {
      const res = await apiClient.get<{ advisors: Advisor[]; cap?: number }>('/advisors')
      setAdvisors(res.data.advisors ?? [])
      setTope(res.data.cap ?? TOPE_POR_DEFECTO)
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'No se pudo cargar el equipo.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
    const id = setInterval(() => { void fetch(true) }, REFRESCO_MS)
    return () => clearInterval(id)
  }, [fetch])

  const enPausa = advisors.filter((a) => !a.available).length
  const sinAtender = advisors
    .map((a) => ({ a, motivos: motivosSinAtender(a) }))
    .filter((x) => x.motivos.length > 0)

  const columnas: Column<Advisor>[] = [
    {
      key: 'asesor',
      header: 'Asesor',
      cell: (a) => <span className="font-semibold">{a.name}</span>,
      sub: (a) => a.email,
    },
    {
      key: 'estado',
      header: 'Disponibilidad',
      cell: (a) => (
        <span className={a.available ? 'badge-green' : 'badge-amber'}>
          {a.available ? 'Recibiendo' : 'En pausa'}
        </span>
      ),
    },
    {
      key: 'atencion',
      header: 'Atención',
      cell: (a) => {
        // Sin el cálculo no se afirma nada: «—», no un «sin esperas» inventado.
        if (a.assigned_live === null || a.waiting_clients === null) {
          return <span className="text-ink-faint" title="No se pudo calcular">—</span>
        }
        const motivos = motivosSinAtender(a)
        if (motivos.length === 0) {
          return <span className="badge-gray">Sin clientes esperando</span>
        }
        return (
          <span className="flex flex-wrap gap-1">
            {motivos.map((m) => <span key={m} className="badge-red">{m}</span>)}
          </span>
        )
      },
      sub: (a) => (a.waiting_clients ?? 0) > 0 && a.waiting_max_min
        ? `El que más espera: ${a.waiting_max_min} min`
        : null,
    },
    {
      key: 'carga',
      header: 'En manos de una persona',
      cell: (a) => (
        <span className="flex items-center gap-1.5">
          <span className="font-mono">{a.active_conversations}</span>
          {/* Solo un indicador: el reparto igual elige al que menos tiene. */}
          {a.over_cap && (
            <span className="badge-amber" title={`Más de ${tope}: no bloquea el reparto, avisa que va cargado`}>
              Sobre el tope de {tope}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'rotacion',
      header: 'Posición en la rotación',
      align: 'right',
      cell: (a) => a.rotation_position
        ? <span className="font-mono">{a.rotation_position}º</span>
        : <span className="text-ink-faint">Fuera de la rotación</span>,
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {sinAtender.length > 0 && (
        <div className="rounded-card border border-danger-border bg-danger-light px-4 py-3 text-body text-danger">
          <p>
            <b>
              {plural(sinAtender.length, 'asesor no está atendiendo', 'asesores no están atendiendo')}
              {' '}a clientes que tiene asignados.
            </b>{' '}
            Las conversaciones siguen a su nombre hasta que alguien las reasigne desde
            En vivo o la bandeja.
          </p>
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {sinAtender.map(({ a, motivos }) => (
              <li key={a.email}><b>{a.name}</b>: {motivos.join(' · ')}</li>
            ))}
          </ul>
        </div>
      )}

      {enPausa > 0 && (
        <p className="rounded-card border border-warn-border bg-warn-light px-4 py-3 text-body text-warn-ink">
          <b>{enPausa} asesor{enPausa === 1 ? '' : 'es'} en pausa.</b>{' '}
          No reciben conversaciones nuevas mientras haya alguien disponible, y conservan las
          que ya tienen abiertas. Si todos están en pausa, las nuevas se asignan igual y esperan
          a que alguien las reasigne.
        </p>
      )}

      <DataTable
        title="Asesores del equipo"
        subtitle={`Ordenados por email. La posición 1 es el próximo en recibir. ${QUE_ES_UN_CLIENTE_ESPERANDO}`}
        columns={columnas}
        rows={advisors}
        rowKey={(a) => a.email}
        loading={loading}
        error={error}
        onRetry={() => void fetch()}
        emptyTitle="No hay asesores en el grupo Asesor"
        emptyMessage="Un admin los agrega desde Administración › Usuarios."
        action={<button className="btn-ghost" onClick={() => void fetch()}>Actualizar</button>}
      />

      <HorarioDeAtencion />
    </div>
  )
}
